import { useEffect, useState } from "react";
import { Alert, Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import useDebouncedSearch from "../shared/hooks/useDebouncedSearch";
import { getGroupByName } from "../../services/serversService";

const FORMATS = ["iso", "clonezilla"];

export default function GenerateNucImageModal({ isOpen, onClose, onSubmit }) {
  const { inputValue, debouncedValue, handleChange, reset } = useDebouncedSearch();
  const [format, setFormat] = useState(FORMATS[0]);
  // Result of the last completed lookup, tagged with the value it applies to so a
  // stale result can never be treated as the answer for the current input.
  // status: "exists" | "missing" | "invalid" | "error"
  const [checkResult, setCheckResult] = useState(null);

  useEffect(() => {
    if (!isOpen || !debouncedValue) {
      setCheckResult(null);
      return undefined;
    }
    let cancelled = false;
    getGroupByName(debouncedValue)
      .then(() => {
        if (!cancelled) setCheckResult({ value: debouncedValue, status: "exists" });
      })
      .catch((ex) => {
        if (cancelled) return;
        const httpStatus = ex.response && ex.response.status;
        if (httpStatus === 404) {
          setCheckResult({ value: debouncedValue, status: "missing" });
        } else if (httpStatus === 400) {
          // The API rejects group names that do not exist and cannot be created.
          const message = (ex.response.data && ex.response.data.detail) || "Invalid group name.";
          setCheckResult({ value: debouncedValue, status: "invalid", message });
        } else {
          setCheckResult({ value: debouncedValue, status: "error" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedValue, isOpen]);

  const handleClose = () => {
    reset();
    setFormat(FORMATS[0]);
    setCheckResult(null);
    onClose();
  };

  const trimmedInput = inputValue.trim();
  // A result only counts when it matches the value currently in the field.
  const settledResult = checkResult && checkResult.value === trimmedInput ? checkResult : null;
  const settledStatus = settledResult ? settledResult.status : null;
  const isCheckPending = !!trimmedInput && settledStatus === null;
  const groupMissing = settledStatus === "missing";
  const nameInvalid = settledStatus === "invalid";
  const checkFailed = settledStatus === "error";

  const handleConfirm = () => {
    if (!trimmedInput || isCheckPending || nameInvalid || checkFailed) {
      return;
    }
    onSubmit(format, trimmedInput);
    handleClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} contentClassName="bg-modal">
      <ModalHeader toggle={handleClose}>Generate image for NUC</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="nuc-image-format">Format</Label>
          <Input
            type="select"
            name="format"
            id="nuc-image-format"
            value={format}
            onChange={(e) => setFormat(e.target.value)}
          >
            {FORMATS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Input>
        </FormGroup>
        <FormGroup>
          <Label for="nuc-image-group">Group</Label>
          <Input
            name="group"
            id="nuc-image-group"
            placeholder="Group name"
            value={inputValue}
            onChange={(e) => handleChange(e.target.value)}
            autoFocus
          />
        </FormGroup>
        {groupMissing && (
          <Alert color="warning">Group &quot;{trimmedInput}&quot; does not exist: it will be created.</Alert>
        )}
        {nameInvalid && <Alert color="danger">{settledResult.message}</Alert>}
        {checkFailed && <Alert color="danger">Could not verify the group. Please try again.</Alert>}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={handleClose}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleConfirm}
          disabled={!trimmedInput || isCheckPending || nameInvalid || checkFailed}
        >
          Generate
        </Button>
      </ModalFooter>
    </Modal>
  );
}
